"use client";

import { RichText } from "@payloadcms/richtext-lexical/react";
import { useLocale, useTranslations } from "next-intl";
import type { BlogForClient } from "@/features/blog/shared/types";
import { Link, useRouter } from "@/i18n/navigation";
import { Image } from "@/lib/client/components/Image";
import { AspectRatio, BackNav, Badge, Container, HStack, Separator, Text, VStack } from "@/ui";
import { BlogViewTracker } from "./BlogViewTracker";

interface PostContainerProps {
  blog: BlogForClient;
  relatedBlogs?: Array<{ id: string; slug: string; title: string }>;
}

/**
 * PostContainer component - Individual blog post detail view
 * Luxury editorial design with rich text rendering
 */
export const PostContainer = ({ blog, relatedBlogs = [] }: PostContainerProps) => {
  const router = useRouter();
  const locale = useLocale();
  const blogT = useTranslations("blog");
  const headerImageAlt = blog.headerImage.alt?.trim();
  return (
    <article className="min-h-screen bg-background">
      <BlogViewTracker
        id={blog.id}
        slug={blog.slug}
        title={blog.title}
      />
      {/* Hero Section */}
      <header className="relative w-full">
        <AspectRatio
          ratio={21 / 9}
          className="relative w-full"
        >
          <Image
            src={blog.headerImage.src}
            alt={
              headerImageAlt && !/^(?:banner|hero|image)$/i.test(headerImageAlt)
                ? headerImageAlt
                : blog.title
            }
            fill
            sizes="100vw"
            className="object-cover object-center"
            priority
          />
          {/* Dark gradient overlay for consistent title contrast on bright images */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
        </AspectRatio>

        {/* Title overlay at bottom of hero */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <Container size="md">
            <VStack className="space-y-4">
              {/* Back Navigation - styled wrapper */}
              <div className="text-white/80 hover:text-white [&>div]:text-white/80 [&>div]:hover:text-white [&_svg]:text-white/80 [&_svg]:hover:text-white">
                <BackNav onBack={() => router.back()} />
              </div>

              {/* Categories */}
              {blog.categories && blog.categories.length > 0 && (
                <HStack className="flex-wrap gap-2">
                  {blog.categories.map((category) => (
                    <Badge
                      key={category}
                      variant="outline"
                      className="rounded-full border-white/40 bg-black/20 px-3 py-1 text-xs text-white backdrop-blur-sm"
                    >
                      {category}
                    </Badge>
                  ))}
                </HStack>
              )}

              {/* Title */}
              <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] md:text-5xl lg:text-6xl">
                {blog.title}
              </h1>

              {/* Date */}
              <Text className="text-sm text-white/70">
                {new Date(blog.updatedAt).toLocaleDateString(locale, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </Text>
            </VStack>
          </Container>
        </div>
      </header>

      {/* Content Section */}
      <Container
        size="sm"
        className="py-12 md:py-20"
      >
        <VStack className="space-y-12">
          {/* Introduction - Lead paragraph style */}
          {blog.introduction && (
            <div className="prose prose-lg max-w-none">
              <RichText
                data={blog.introduction}
                className="prose prose-lg max-w-none text-lg leading-relaxed text-foreground/90 md:text-xl"
              />
            </div>
          )}

          <Separator className="bg-border/50" />

          {/* Main Content */}
          {blog.content && (
            <div className="prose prose-lg max-w-none">
              <RichText
                data={blog.content}
                className="prose prose-lg max-w-none"
              />
            </div>
          )}

          {relatedBlogs.length > 0 && (
            <section aria-labelledby="related-blog-posts">
              <h2
                id="related-blog-posts"
                className="font-serif text-2xl font-semibold"
              >
                {blogT("title")}
              </h2>
              <nav aria-label={blogT("title")}>
                <ul className="mt-4 grid gap-3 md:grid-cols-3">
                  {relatedBlogs.map((relatedBlog) => (
                    <li key={relatedBlog.id}>
                      <Link
                        className="font-medium underline-offset-4 hover:underline"
                        href={`/blog/${relatedBlog.slug}`}
                      >
                        {relatedBlog.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </section>
          )}
        </VStack>
      </Container>
    </article>
  );
};
